import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import FormLabel from "@mui/material/FormLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { trim } from "lodash";
import InfoIcon from "@mui/icons-material/Info";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../../common/components/Helper/Utils";
import { editPayment } from "./../../dashboard/actions/dashboardAction";
import amountFormatter from "../../common/components/amountFormat";

const EDITABLE_PAYMENT_METHODS = [
  "Affirm",
  "Cash",
  "Check",
  "Credit Card",
  "Crypto",
  "Paypal",
  "Scrap Gold",
  "Venmo",
  "Wire",
  "Zelle",
];

const isQboAccountVerified = () =>
  window.sessionStorage.getItem("isQboVerified") === "true";

/** Modal tip only when account is QBO-verified AND invoice/payment is QBO-linked. */
const isQboSyncedPayment = (invoice, payment) => {
  if (!isQboAccountVerified()) return false;
  return (
    invoice?.is_invoice_qbo_synced === true ||
    payment?.is_qbo_synced === true ||
    payment?.is_invoice_qbo_synced === true ||
    payment?.qbo_synced === true ||
    payment?.isQboLinked === true
  );
};

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const raw = value.toString();
  // MM/DD/YYYY
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (match) {
    const [, mm, dd, yyyy] = match;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  }
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [yyyy, mm, dd] = raw.slice(0, 10).split("-");
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateForApi = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : parseDateValue(date);
  if (!d || Number.isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

export default function EditPaymentModal(props) {
  const { open, handleClose, invoice, payment, onEditComplete } = props;
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [notes, setNotes] = useState("");
  const [errorText, setErrorText] = useState("");
  const [amountError, setAmountError] = useState("");
  const [methodError, setMethodError] = useState("");
  const [disabled, setDisabled] = useState(false);

  const showQboNote = isQboSyncedPayment(invoice, payment);
  const paymentId = payment?.id ?? payment?.paymentid ?? payment?.paymentId;
  const isTradePayment =
    (payment?.paymentmethod || "").toString().toLowerCase() === "trade";
  const paymentMethodOptions = isTradePayment
    ? ["Trade"]
    : EDITABLE_PAYMENT_METHODS;
  const oldAmount = parseFloat(payment?.amount) || 0;
  const invoiceTotal = parseFloat(invoice?.totalprice) || 0;
  const invoiceBalance = parseFloat(invoice?.balance) || 0;
  const paidAmount = invoiceTotal - invoiceBalance;

  const isSelectedTrade =
    (paymentMethod || "").toString().toLowerCase() === "trade" || isTradePayment;

  const maxAllowedAmount = useMemo(
    () => Number((invoiceBalance + oldAmount).toFixed(2)),
    [invoiceBalance, oldAmount]
  );

  const remainingAmount = useMemo(() => {
    const parsedAmount = parseFloat(amount);
    const nextAmount = Number.isNaN(parsedAmount) ? 0 : parsedAmount;
    // Match Add Payment UX: for non-Trade overpay, keep Remaining at available balance
    // instead of showing a negative value.
    if (!isSelectedTrade && nextAmount > maxAllowedAmount) {
      return maxAllowedAmount.toFixed(2);
    }
    return (invoiceBalance + oldAmount - nextAmount).toFixed(2);
  }, [
    amount,
    invoiceBalance,
    oldAmount,
    isSelectedTrade,
    maxAllowedAmount,
  ]);

  const validateAmountAgainstBalance = (nextAmountValue, methodValue) => {
    const parsedAmount = parseFloat(nextAmountValue);
    if (
      nextAmountValue === "" ||
      nextAmountValue === null ||
      nextAmountValue === undefined ||
      Number.isNaN(parsedAmount)
    ) {
      return "";
    }
    if (parsedAmount <= 0) {
      return "Please enter a valid amount greater than 0.";
    }
    const selectedIsTrade =
      (methodValue || "").toString().toLowerCase() === "trade" || isTradePayment;
    if (!selectedIsTrade && parsedAmount > maxAllowedAmount) {
      return "Amount should not exceed remaining amount";
    }
    return "";
  };

  useEffect(() => {
    if (open && payment) {
      setAmount(
        payment?.amount !== null && payment?.amount !== undefined
          ? String(payment.amount)
          : ""
      );
      setPaymentDate(
        parseDateValue(payment?.paymentdate || payment?.createdon)
      );
      setPaymentMethod(payment?.paymentmethod || "");
      setPaymentDetails(
        payment?.paymentmethod_details && payment.paymentmethod_details !== "-"
          ? payment.paymentmethod_details
          : ""
      );
      const existingNotes =
        payment?.notes && payment.notes !== "-"
          ? payment.notes
          : payment?.edit_reason ||
            payment?.editReason ||
            payment?.last_edit_reason ||
            "";
      setNotes(existingNotes || "");
      setErrorText("");
      setAmountError("");
      setMethodError("");
      setDisabled(false);
    }
  }, [open, payment]);

  const submitEditPayment = (event) => {
    event.preventDefault();
    setErrorText("");
    setAmountError("");
    setMethodError("");

    const parsedAmount = parseFloat(amount);
    if (!amount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setAmountError("Please enter a valid amount greater than 0.");
      return;
    }
    if (!paymentMethod) {
      setMethodError("Please select a payment method.");
      return;
    }
    const overpayError = validateAmountAgainstBalance(parsedAmount, paymentMethod);
    if (overpayError) {
      setAmountError(overpayError);
      return;
    }
    if (notes.length >= 1000) {
      setErrorText("Notes cannot exceed 1000 characters.");
      return;
    }
    if (!paymentId) {
      showErrorToast("Payment ID is missing. Cannot edit this payment.");
      return;
    }

    setDisabled(true);
    const trimmedNotes = trim(notes);
    const data = {
      paymentId,
      invoiceId: invoice?.id,
      accountId:
        invoice?.accountid || window.sessionStorage.getItem("accountId"),
      userId: invoice?.userid,
      amount: parsedAmount,
      paymentDate: formatDateForApi(paymentDate),
      paymentMethod,
      paymentmethod_details: trim(paymentDetails),
      remainingBalance: remainingAmount,
      notes: trimmedNotes,
      old_amount: payment?.amount,
      old_paymentMethod: payment?.paymentmethod,
    };

    editPayment(data, (err, results) => {
      setDisabled(false);
      if (err) {
        showErrorToast(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to edit payment"
        );
        return;
      }
      if (results?.success === 0 || results?.status === 0) {
        showErrorToast(results?.message || "Failed to edit payment");
        return;
      }

      // Same QBO contract as void: soft-update in WatchOps + top-right warning when QBO-linked
      const payload = results?.data || {};
      const message =
        results?.message || "Payment updated successfully in WatchOps.";
      const isQboManualFlag = (value) =>
        value === true || value === 1 || value === "true" || value === "1";
      const qboManualRequired =
        isQboManualFlag(results?.qboManualVoidRequired) ||
        isQboManualFlag(payload?.qboManualVoidRequired) ||
        isQboManualFlag(results?.qboManualEditRequired) ||
        isQboManualFlag(payload?.qboManualEditRequired) ||
        isQboManualFlag(payload?.isQboLinked) ||
        showQboNote;
      const qboWarning =
        payload?.qboWarning ||
        results?.qboWarning ||
        "Payment updated in WatchOps. This edit is not reflecting in QuickBooks — please update the payment manually in QuickBooks.";

      if (qboManualRequired) {
        showSuccessToast("Payment updated successfully in WatchOps.");
        showWarningToast(qboWarning);
      } else {
        showSuccessToast(message);
      }

      handleClose();
      onEditComplete?.(results);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-payment-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="edit-payment-dialog-title">
        Edit Payment
        <IconButton onClick={handleClose} style={{ float: "right" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={submitEditPayment}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: 8,
            }}
          >
            <Typography variant="subtitle2">
              Total Invoice Amount: $
              {invoiceTotal
                ? amountFormatter(Number(invoiceTotal))
                : invoice?.totalprice || "-"}
            </Typography>
            <Typography variant="subtitle2">
              Paid Amount: $
              {!Number.isNaN(paidAmount)
                ? amountFormatter(Number(paidAmount.toFixed(2)))
                : "-"}
            </Typography>
          </div>

          <FormControl
            className="form-width-100 margin-t-b-20 margin-p-b-20"
            fullWidth
            style={{ marginTop: 15 }}
          >
            <InputLabel
              htmlFor="edit-payment-amount"
              shrink={!!amount}
              className="color-BCBCBC"
            >
              Amount
            </InputLabel>
            <Input
              id="edit-payment-amount"
              value={amount}
              onChange={(e) => {
                const clean = e.target.value.replace(/[^0-9.]/g, "");
                setAmount(clean);
                setAmountError(
                  validateAmountAgainstBalance(clean, paymentMethod)
                );
              }}
            />
            {amountError ? (
            <FormHelperText error style={{ color: "#d32f2f", marginLeft: 0 }}>
                {amountError}
              </FormHelperText>
            ) : null}
          </FormControl>

          <FormControl
            className="form-width-100 margin-t-b-20 margin-p-b-20"
            fullWidth
            style={{ marginTop: 15 }}
          >
            <FormLabel style={{ color: "#C2BCBC" }}>Date</FormLabel>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                margin="normal"
                id="edit-payment-date"
                format="MM/dd/yyyy"
                value={paymentDate}
                maxDate={new Date()}
                onChange={(date) => setPaymentDate(date)}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
                placeholder="MM/DD/YYYY"
                onPaste={(event) => event.preventDefault()}
                helperText=""
              />
            </MuiPickersUtilsProvider>
          </FormControl>

          <FormControl fullWidth style={{ marginTop: 15 }}>
            <InputLabel htmlFor="edit-payment-method" className="color-BCBCBC">
              Payment Method
            </InputLabel>
            <Select
              id="edit-payment-method"
              value={paymentMethod}
              disabled={isTradePayment}
              onChange={(e) => {
                const nextMethod = e.target.value;
                setPaymentMethod(nextMethod);
                setMethodError("");
                setAmountError(
                  validateAmountAgainstBalance(amount, nextMethod)
                );
              }}
              label="Payment Method"
            >
              {[...paymentMethodOptions]
                .sort((a, b) => a.localeCompare(b))
                .map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
            </Select>
            <FormHelperText className="error-o">{methodError}</FormHelperText>
          </FormControl>

          <FormControl
            className="form-width-100 margin-t-b-20 margin-p-b-20"
            fullWidth
            style={{ marginTop: 15 }}
          >
            <InputLabel
              htmlFor="edit-payment-details"
              shrink={!!paymentDetails}
              className="color-BCBCBC"
            >
              Payment Details
            </InputLabel>
            <Input
              id="edit-payment-details"
              value={paymentDetails}
              onChange={(e) => setPaymentDetails(e.target.value)}
            />
          </FormControl>

          <FormControl
            className="form-width-100 margin-t-b-20 margin-p-b-20"
            fullWidth
            style={{ marginTop: 15 }}
          >
            <InputLabel
              htmlFor="edit-payment-notes"
              shrink={!!notes}
              className="color-BCBCBC"
            >
              Notes
            </InputLabel>
            <Input
              id="edit-payment-notes"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setErrorText("");
              }}
            />
            <FormHelperText className="error-o">{errorText}</FormHelperText>
          </FormControl>

          <FormControl
            className="form-width-99 margin-t-b-20 margin-p-b-20"
            fullWidth
            style={{ marginTop: 15 }}
          >
            <InputLabel
              htmlFor="edit-payment-remaining"
              shrink
              className="color-BCBCBC"
            >
              Remaining Amount
            </InputLabel>
            <Input
              id="edit-payment-remaining"
              readOnly
              value={remainingAmount}
            />
          </FormControl>

          {showQboNote && (
            <p
              style={{
                display: "flex",
                alignItems: "flex-start",
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.5,
                margin: "16px 0 0 0",
                color: "#b26a00",
              }}
            >
              <InfoIcon
                style={{
                  marginRight: 8,
                  marginTop: 2,
                  fontSize: 20,
                  color: "#b26a00",
                  flexShrink: 0,
                }}
              />
              This edit payment will not be reflected in QuickBooks. Please update this
              payment manually in QuickBooks.
            </p>
          )}

          <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 24,
              marginBottom: 8,
              gap: 8,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={disabled || !!amountError}
              className="text-capitalize primary-bg color-white"
              endIcon={disabled ? <CircularProgress style={{ width: 22, height: 22, color: '#fff' }} /> : null}
            >
              SUBMIT
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleClose}
              className="text-capitalize"
            >
              EXIT
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
}
